import React, { useState, useEffect } from 'react';
import { GameEngine } from '../game/engine.js';
import { NetworkClient } from '../network/client.js';
import { AudioManager } from '../audio/manager.js';
import { HUD } from './hud.js';
import { MainMenu } from './main-menu/MainMenu.js';
import { MatchmakingStatus } from './main-menu/MatchmakingStatus.js';
import { UpgradeBar } from './hud-upgrades/UpgradeBar.js';
import { Leaderboard } from './leaderboard.js';
import { GamePhase, PlayerState } from '@push.io/shared';
import { getInitialControlScheme, type MenuControlScheme } from './main-menu/types.js';
import type { MenuLanguage } from './main-menu/types.js';
import type { TutorialStep } from '../tutorial/TutorialManager.js';
import { saveTutorialChoice } from '../tutorial/tutorial-storage.js';
import { TutorialOverlay } from './tutorial/TutorialOverlay.js';
import { useAccountController, useAccountSnapshot, useHydrateWelcomeAccount } from '../account/context.js';
import { CustomizationScreen } from './customization/CustomizationScreen.js';
import type { PublicPlayerAppearance, UserLoadoutDTO } from '@push.io/shared';
import { BotMatchSetup } from './main-menu/BotMatchSetup.js';
import type { BotDifficulty } from '../game/bot-ai.js';
import type { BotMatchResult } from '../game/bot-match.js';
import { BotMatchResults } from './bot-match/BotMatchResults.js';

interface AppProps {
  gameEngine: GameEngine;
  network: NetworkClient;
  audio: AudioManager;
}

enum Screen {
  Loading = 'loading',
  Lobby = 'lobby',
  Customization = 'customization',
  Playing = 'playing',
  Tutorial = 'tutorial',
  TutorialComplete = 'tutorial-complete',
  Results = 'results',
  BotSetup = 'bot-setup',
  BotMatch = 'bot-match',
}

export function App({ gameEngine, network, audio }: AppProps) {
  const account = useAccountController();
  const accountSnapshot = useAccountSnapshot();
  const hydrateWelcomeAccount = useHydrateWelcomeAccount();
  const [screen, setScreen] = useState<Screen>(Screen.Loading);
  const [phase, setPhase] = useState<GamePhase>('lobby');
  const [countdownSeconds, setCountdownSeconds] = useState<number>(0);
  const [localPlayer, setLocalPlayer] = useState<PlayerState | null>(null);
  const [players, setPlayers] = useState<PlayerState[]>([]);
  const [matchResult, setMatchResult] = useState<any>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [controlScheme, setControlScheme] = useState<MenuControlScheme>(getInitialControlScheme);
  const [tutorialStep, setTutorialStep] = useState<TutorialStep | null>(null);
  const [tutorialFocusPosition, setTutorialFocusPosition] =
    useState<{ x: number; y: number } | null>(null);
  const [tutorialLanguage, setTutorialLanguage] = useState<MenuLanguage>(() => {
    try { return JSON.parse(localStorage.getItem('pushio_menu_settings') || '{}').language || 'es'; }
    catch { return 'es'; }
  });
  const [reducedMotion, setReducedMotion] = useState(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('pushio_menu_settings') || '{}');
      return !!settings.reduceCameraShake || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch { return false; }
  });
  const [botResult, setBotResult] = useState<BotMatchResult | null>(null);
  const [botConfig, setBotConfig] = useState<{ botCount: number; difficulty: BotDifficulty } | null>(null);

  useEffect(() => {
    if (screen === Screen.Playing || screen === Screen.Tutorial || screen === Screen.BotMatch) {
      audio.playGameMusic();
    } else {
      audio.playMenuMusic();
    }
  }, [screen, audio]);

  useEffect(() => {
    network.onWelcomeEvent((data: any) => {
      const pid = data.playerId || network.getPlayerId();
      console.log('[App] Welcome received, playerId:', pid);
      setScreen(Screen.Lobby);
      gameEngine.setInputEnabled(false);
      const state = gameEngine.getState();
      state.setPlayerId(pid || '');
      hydrateWelcomeAccount(data);
      void account.refreshAll();

      if (data.loadout) {
        state.applyPlayerAppearances([{
          playerId: pid || '',
          appearance: data.loadout,
        }]);
      }
    });

    network.onMatchStartEvent((data: any) => {
      setScreen(Screen.Playing);
      setPhase('playing');
      setMatchResult(null);
      gameEngine.setInputEnabled(true);
      gameEngine.resetCamera();

      if (data.players) {
        gameEngine.getState().applyPlayerAppearances(data.players.map((p: any) => ({
          playerId: p.playerId,
          appearance: p.appearance,
        })));
      }
    });

    network.onMatchEndEvent((data: any) => {
      setPhase('ended');
      gameEngine.setInputEnabled(false);
      if (data.winnerId === network.getPlayerId()) {
        audio.playVictory();
      }
    });

    network.onMatchResultUpdatedEvent((data: any) => {
      setMatchResult(data);
      setScreen(Screen.Results);
      account.updateWalletFromServer(data.balanceAfter);
    });

    network.onWalletUpdatedEvent((data: any) => {
      if (data?.softCurrency !== undefined) {
        account.updateWalletFromServer(data.softCurrency);
      }
    });

    network.onSnapshotEvent((data: any) => {
      const state = gameEngine.getState();
      const prevLp = state.getLocalPlayer();
      const prevAlive = prevLp?.alive ?? true;
      const prevOrbs = prevLp?.orbs || 0;
      const previousWorldOrbs = [...state.orbs];
      state.applySnapshot(data);
      gameEngine.onSnapshotApplied();
      const lp = state.getLocalPlayer();
      setLocalPlayer(lp ? { ...lp } : null);
      setPlayers(Array.from(state.players.values()));
      if (lp) {
        if (prevAlive && !lp.alive) {
        }
        if (lp.orbs > prevOrbs) {
          audio.playOrbCollect();
          const remainingIds = new Set(state.orbs.map((orb) => orb.id));
          const collectedOrb = previousWorldOrbs
            .filter((orb) => !remainingIds.has(orb.id))
            .sort((a, b) =>
              Math.hypot(a.x - lp.x, a.y - lp.y) - Math.hypot(b.x - lp.x, b.y - lp.y))[0];
          if (collectedOrb) gameEngine.showOrbCollection(collectedOrb.x, collectedOrb.y);
        }
      }
    });

    network.onMatchStateEvent((data: any) => {
      if (data.phase) setPhase(data.phase);
      if (data.seconds !== undefined) setCountdownSeconds(data.seconds);
    });

    network.onPlayerCollisionEvent((event) => {
      gameEngine.showCollision(event);
      if (event.playerAId === network.getPlayerId() || event.playerBId === network.getPlayerId()) {
        audio.playImpact();
      }
    });
  }, [account, gameEngine, hydrateWelcomeAccount, network, audio]);

  useEffect(() => {
    const playerId = network.getPlayerId();
    const loadout = accountSnapshot.loadout;
    if (!playerId || !loadout) return;
    const state = gameEngine.getState();
    state.applyPlayerAppearances([{ playerId, appearance: normalizeAppearance(loadout) }]);
  }, [accountSnapshot.loadout, gameEngine, network]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (screen === Screen.BotMatch && e.key === 'Escape') {
        setShowLeaveConfirm(true);
        return;
      }
      if (screen !== Screen.Playing) return;
      if (e.key === 'Tab') {
        e.preventDefault();
      }
      if (e.key === 'Escape' && phase === 'playing') {
        setShowLeaveConfirm(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [screen, phase]);

  const leaveTutorial = (choice: 'completed' | 'skipped') => {
    saveTutorialChoice(choice);
    gameEngine.stopTutorial(network.getPlayerId() || '');
    setTutorialStep(null);
    setTutorialFocusPosition(null);
    setLocalPlayer(null);
    setPlayers([]);
    setMatchResult(null);
  };

  const enterMatchmaking = (choice: 'completed' | 'skipped') => {
    leaveTutorial(choice);
    setScreen(Screen.Lobby);
    network.joinQueue();
  };

  const completeTutorial = () => {
    leaveTutorial('completed');
    setScreen(Screen.TutorialComplete);
  };

  const startTutorial = () => {
    network.leaveQueue();
    try {
      const currentSettings = JSON.parse(localStorage.getItem('pushio_menu_settings') || '{}');
      setTutorialLanguage(currentSettings.language || 'es');
      setReducedMotion(!!currentSettings.reduceCameraShake
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      const mobileOrTablet = window.matchMedia('(pointer: coarse)').matches
        || (navigator.maxTouchPoints > 0 && Math.min(window.innerWidth, window.innerHeight) <= 1024);
      const tutorialControls: MenuControlScheme = mobileOrTablet
        ? 'touch'
        : currentSettings.controlScheme === 'mouse' ? 'mouse' : 'wasd';
      setControlScheme(tutorialControls);
      gameEngine.setControlScheme(tutorialControls);
    } catch {
      setTutorialLanguage('es');
      const mobileOrTablet = window.matchMedia('(pointer: coarse)').matches
        || (navigator.maxTouchPoints > 0 && Math.min(window.innerWidth, window.innerHeight) <= 1024);
      const tutorialControls: MenuControlScheme = mobileOrTablet ? 'touch' : 'wasd';
      setControlScheme(tutorialControls);
      gameEngine.setControlScheme(tutorialControls);
    }
    setScreen(Screen.Tutorial);
    setPhase('playing');
    setMatchResult(null);
    gameEngine.startTutorial({
      onUpdate: (step, player, tutorialPlayers) => {
        setTutorialStep(step);
        setLocalPlayer(player);
        setPlayers(tutorialPlayers);
        const state = gameEngine.getState();
        const upgradeIndex = {
          'upgrade-push': 0, 'upgrade-weight': 1, 'upgrade-speed': 2, 'upgrade-control': 3,
        }[step.highlight as 'upgrade-push' | 'upgrade-weight' | 'upgrade-speed' | 'upgrade-control'];
        const upgradeButton = upgradeIndex !== undefined
          ? document.querySelectorAll<HTMLElement>('.upgrade-button')[upgradeIndex]
          : null;
        const upgradeRect = upgradeButton?.getBoundingClientRect();
        const target = step.highlight === 'orb'
          ? state.orbs[0]
          : step.highlight === 'bot' ? state.players.get('practice-bot') : null;
        setTutorialFocusPosition(target
          ? gameEngine.worldToScreen(target.x, target.y)
          : upgradeRect ? { x: upgradeRect.left + upgradeRect.width / 2,
            y: upgradeRect.top + upgradeRect.height / 2 } : null);
      },
      onComplete: completeTutorial,
    });
  };

  const handleVoluntaryLeave = () => {
    setShowLeaveConfirm(false);
    network.sendVoluntaryLeave();
    gameEngine.setInputEnabled(false);
  };

  const handlePlayAgain = () => {
    network.joinQueue();
  };

  const handleMainMenu = () => {
    setMatchResult(null);
    network.leaveQueue();
    setScreen(Screen.Lobby);
  };

  const startBotMatch = (botCount: number, difficulty: BotDifficulty) => {
    network.leaveQueue();
    setShowLeaveConfirm(false);
    setBotConfig({ botCount, difficulty });
    setBotResult(null);
    try {
      const currentSettings = JSON.parse(localStorage.getItem('pushio_menu_settings') || '{}');
      const mobileOrTablet = window.matchMedia('(pointer: coarse)').matches
        || (navigator.maxTouchPoints > 0 && Math.min(window.innerWidth, window.innerHeight) <= 1024);
      const botControls: MenuControlScheme = mobileOrTablet
        ? 'touch'
        : currentSettings.controlScheme === 'mouse' ? 'mouse' : 'wasd';
      setControlScheme(botControls);
      gameEngine.setControlScheme(botControls);
    } catch {
      const mobileOrTablet = window.matchMedia('(pointer: coarse)').matches
        || (navigator.maxTouchPoints > 0 && Math.min(window.innerWidth, window.innerHeight) <= 1024);
      const botControls: MenuControlScheme = mobileOrTablet ? 'touch' : 'wasd';
      setControlScheme(botControls);
      gameEngine.setControlScheme(botControls);
    }

    const playerName = localStorage.getItem('pushio_player_name')?.trim() || 'Player';
    const appearance = accountSnapshot.loadout
      ? normalizeAppearance(accountSnapshot.loadout)
      : { baseColorId: 'base_color_blue' as const, skinId: null, patternId: null, flagId: null,
        letterId: null, accessoryId: null, accessoryColor: null, trailId: null, nameplateId: null,
        badgeId: null, victoryEffectId: null };

    setPhase('playing');
    setScreen(Screen.BotMatch);
    setLocalPlayer(null);
    setPlayers([]);
    gameEngine.startBotMatch({
      botCount,
      difficulty,
      playerId: network.getPlayerId() || `local_${Date.now()}`,
      playerName,
      playerAppearance: appearance,
    }, {
      onUpdate: (player, botPlayers) => {
        setLocalPlayer(player);
        setPlayers(botPlayers);
      },
      onEnd: (result) => {
        setBotResult(result);
        setPhase('ended');
        gameEngine.setInputEnabled(false);
        if (result.won) audio.playVictory();
      },
    });
  };

  const leaveBotMatch = () => {
    gameEngine.stopBotMatch(network.getPlayerId() || '');
    setShowLeaveConfirm(false);
    setBotResult(null);
    setLocalPlayer(null);
    setPlayers([]);
    setScreen(Screen.Lobby);
  };

  const replayBotMatch = (result: BotMatchResult) => {
    void result;
    if (botConfig) startBotMatch(botConfig.botCount, botConfig.difficulty);
  };

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      {screen === Screen.Loading && <LoadingScreen />}
      {screen === Screen.Lobby && <MainMenu network={network} gameEngine={gameEngine} audio={audio}
        wallet={accountSnapshot.wallet}
        onStartTutorial={startTutorial}
        onStartBots={() => {
          try {
            setTutorialLanguage(JSON.parse(localStorage.getItem('pushio_menu_settings') || '{}').language || 'es');
          } catch {
            setTutorialLanguage('es');
          }
          setScreen(Screen.BotSetup);
        }}
        onCustomize={() => {
          try {
            setTutorialLanguage(JSON.parse(localStorage.getItem('pushio_menu_settings') || '{}').language || 'es');
          } catch {
            setTutorialLanguage('es');
          }
          setScreen(Screen.Customization);
        }} />}
      {screen === Screen.BotSetup && (
        <BotMatchSetup
          language={tutorialLanguage}
          onStart={(botCount, difficulty) => startBotMatch(botCount, difficulty)}
          onBack={() => setScreen(Screen.Lobby)}
        />
      )}
      {screen === Screen.Customization && (
        <CustomizationScreen
          language={tutorialLanguage}
          onBack={() => setScreen(Screen.Lobby)}
        />
      )}
      {screen === Screen.Playing && (
        <>
          <HUD
            localPlayer={localPlayer}
            phase={phase}
            countdownSeconds={countdownSeconds}
            onLeave={() => setShowLeaveConfirm(true)}
            language={tutorialLanguage}
          />
          <UpgradeBar player={localPlayer} network={network} audio={audio} />
          <Leaderboard players={players} localPlayerId={localPlayer?.id || ''} />
          {showLeaveConfirm && (
            <LeaveConfirmDialog
              language={tutorialLanguage}
              onConfirm={handleVoluntaryLeave}
              onCancel={() => setShowLeaveConfirm(false)}
            />
          )}
        </>
      )}
      {screen === Screen.BotMatch && (
        <>
          <HUD localPlayer={localPlayer} phase={phase} countdownSeconds={0}
            onLeave={() => setShowLeaveConfirm(true)} language={tutorialLanguage} />
          <UpgradeBar player={localPlayer} network={network} audio={audio}
            onBotMatchPurchase={(stat) => gameEngine.purchaseBotMatchUpgrade(stat)} />
          <Leaderboard players={players} localPlayerId={localPlayer?.id || ''} />
          {showLeaveConfirm && !botResult && (
            <LeaveConfirmDialog
              language={tutorialLanguage}
              isBotMatch
              onConfirm={leaveBotMatch}
              onCancel={() => setShowLeaveConfirm(false)}
            />
          )}
          {botResult && (
            <BotMatchResults
              result={botResult}
              language={tutorialLanguage}
              onReplay={() => {
                setShowLeaveConfirm(false);
                replayBotMatch(botResult);
              }}
              onMenu={() => {
                setShowLeaveConfirm(false);
                leaveBotMatch();
              }}
            />
          )}
        </>
      )}
      {screen === Screen.Tutorial && tutorialStep && (
        <>
          <HUD localPlayer={localPlayer} phase="playing" countdownSeconds={0} />
          <UpgradeBar player={localPlayer} network={network} audio={audio}
            tutorialPurchaseEnabled={tutorialStep.id === 'upgrade'}
            onTutorialPurchase={(stat) => gameEngine.purchaseTutorialUpgrade(stat)} />
          <TutorialOverlay step={tutorialStep} language={tutorialLanguage}
            controlScheme={controlScheme}
            focusPosition={tutorialFocusPosition}
            reducedMotion={reducedMotion}
            onContinue={() => gameEngine.continueTutorialExplanation()}
            onSkip={() => enterMatchmaking('skipped')} />
        </>
      )}
      {screen === Screen.TutorialComplete && (
        <TutorialCompleteScreen language={tutorialLanguage}
          onMenu={() => {
            network.leaveQueue();
            setScreen(Screen.Lobby);
          }}
          onMatchmaking={() => {
            setScreen(Screen.Lobby);
            network.joinQueue();
          }} />
      )}
      {screen === Screen.Results && (
        <ResultsScreen
          result={matchResult}
          network={network}
          language={tutorialLanguage}
          onPlayAgain={handlePlayAgain}
          onMainMenu={handleMainMenu}
        />
      )}
    </div>
  );
}

function normalizeAppearance(loadout: UserLoadoutDTO): PublicPlayerAppearance {
  return {
    baseColorId: loadout.baseColorId || 'base_color_blue',
    skinId: loadout.skinId,
    patternId: loadout.patternId,
    flagId: loadout.flagId,
    letterId: loadout.letterId,
    accessoryId: loadout.accessoryId,
    accessoryColor: loadout.accessoryColor,
    trailId: loadout.trailId,
    nameplateId: loadout.nameplateId,
    badgeId: loadout.badgeId,
    victoryEffectId: loadout.victoryEffectId,
  };
}

function LeaveConfirmDialog({ language, isBotMatch = false, onConfirm, onCancel }: {
  language: MenuLanguage;
  isBotMatch?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const tr = (es: string, en: string, fr: string) =>
    language === 'es' ? es : language === 'fr' ? fr : en;
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)',
    }}>
      <div style={{
        background: 'linear-gradient(160deg,rgba(29,29,62,.99),rgba(15,15,34,.99))',
        border: '1px solid rgba(255,68,68,.35)', borderRadius: '16px',
        padding: '28px', width: 'min(90vw, 380px)', textAlign: 'center',
        color: '#fff', fontFamily: 'monospace',
      }}>
        <h2 style={{ margin: 0, fontSize: '20px', color: '#ff6644' }}>
          {tr('¿Abandonar la partida?', 'Leave the match?', 'Quitter la partie ?')}
        </h2>
        <p style={{ color: '#8b8ba2', fontSize: '13px', lineHeight: 1.5, margin: '12px 0 20px' }}>
          {isBotMatch
            ? tr(
              'La partida actual se perderá y volverás al menú principal.',
              'The current match will be lost and you will return to the main menu.',
              'La partie en cours sera perdue et vous reviendrez au menu principal.'
            )
            : tr(
              'Perderás 5 monedas. Tu saldo nunca bajará de 0.',
              'You will lose 5 coins. Your balance will never go below 0.',
              'Vous perdrez 5 pièces. Votre solde ne descendra jamais en dessous de 0.'
            )}
        </p>
        <button onClick={onCancel} style={{
          width: '100%', padding: '12px', border: 0, borderRadius: '10px',
          background: 'linear-gradient(135deg,#4444ff,#6262ff)', color: '#fff',
          font: '800 13px monospace', cursor: 'pointer', marginBottom: '8px',
        }}>
          {tr('Seguir jugando', 'Keep playing', 'Continuer à jouer')}
        </button>
        <button onClick={onConfirm} style={{
          width: '100%', padding: '12px',
          border: '1px solid rgba(255,68,68,.4)', borderRadius: '10px',
          background: 'transparent', color: '#ff6644',
          font: '800 13px monospace', cursor: 'pointer',
        }}>
          {tr('Abandonar', 'Leave', 'Quitter')}
        </button>
      </div>
    </div>
  );
}

function TutorialCompleteScreen({ language, onMenu, onMatchmaking }: {
  language: MenuLanguage;
  onMenu: () => void;
  onMatchmaking: () => void;
}) {
  const tr = (es: string, en: string, fr: string) =>
    language === 'es' ? es : language === 'fr' ? fr : en;
  return <div style={{
    position: 'absolute', inset: 0, zIndex: 70,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', background: 'rgba(7,7,22,.94)', color: '#fff', fontFamily: 'monospace',
  }}>
    <section style={{
      width: 'min(92vw, 460px)', padding: '36px', textAlign: 'center',
      border: '1px solid rgba(102,102,255,.35)', borderRadius: '22px',
      background: 'linear-gradient(160deg,rgba(29,29,62,.99),rgba(15,15,34,.99))',
      boxShadow: '0 30px 90px rgba(0,0,0,.6)',
    }}>
      <div style={{ fontSize: '40px', color: '#00ff88', marginBottom: '12px' }}>✓</div>
      <h1 style={{ margin: 0, fontSize: '27px' }}>
        {tr('Ya estás preparado', "You're ready", 'Vous êtes prêt')}
      </h1>
      <p style={{ color: '#8b8ba2', fontSize: '13px', lineHeight: 1.5, margin: '12px 0 27px' }}>
        {tr('Tutorial completado. ¿Qué quieres hacer ahora?', 'Tutorial complete. What would you like to do now?', 'Tutoriel terminé. Que voulez-vous faire maintenant ?')}
      </p>
      <button onClick={onMatchmaking} style={{
        width: '100%', padding: '15px', border: 0, borderRadius: '11px',
        background: 'linear-gradient(135deg,#4444ff,#6262ff)', color: '#fff',
        font: '800 14px monospace', cursor: 'pointer',
      }}>
        {tr('Buscar partida', 'Find match', 'Chercher une partie')}
      </button>
      <button onClick={onMenu} style={{
        width: '100%', marginTop: '10px', padding: '12px',
        border: '1px solid rgba(255,255,255,.1)', borderRadius: '11px',
        background: 'transparent', color: '#9999af', font: '800 13px monospace', cursor: 'pointer',
      }}>
        {tr('Ir al menú principal', 'Go to main menu', 'Aller au menu principal')}
      </button>
    </section>
  </div>;
}

function LoadingScreen() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', color: '#ffffff', fontFamily: 'monospace', fontSize: '24px',
    }}>
      Conectando...
    </div>
  );
}

function ResultsScreen({ result, network, language, onPlayAgain, onMainMenu }: {
  result: any;
  network: NetworkClient;
  language: MenuLanguage;
  onPlayAgain: () => void;
  onMainMenu: () => void;
}) {
  const [queued, setQueued] = useState(false);
  const [queueRequested, setQueueRequested] = useState(false);
  const [autoQueueSeconds, setAutoQueueSeconds] = useState(30);
  const [autoQueueDeadline] = useState(() => Date.now() + 30000);
  const [queueStatus, setQueueStatus] = useState({ position: 0, playersWaiting: 0, estimatedWait: 0 });
  const tr = (spanish: string, english: string, french: string) =>
    language === 'es' ? spanish : language === 'fr' ? french : english;

  useEffect(() => {
    network.onQueueStatusEvent((status: any) => {
      if (!status.inQueue) return;
      setQueueStatus({
        position: status.position || 0,
        playersWaiting: status.playersWaiting || 0,
        estimatedWait: status.estimatedWait || 0,
      });
      setQueued(true);
      setQueueRequested(false);
    });
    return () => network.onQueueStatusEvent(null);
  }, [network]);

  useEffect(() => {
    if (!queueRequested && !queued) return;
    network.requestQueueStatus();
    const timer = window.setInterval(() => network.requestQueueStatus(), 1500);
    return () => clearInterval(timer);
  }, [network, queueRequested, queued]);

  useEffect(() => {
    if (queued || queueRequested) return;
    const refresh = () => {
      setAutoQueueSeconds(Math.max(0, Math.ceil((autoQueueDeadline - Date.now()) / 1000)));
    };
    refresh();
    const timer = window.setInterval(refresh, 200);
    return () => window.clearInterval(timer);
  }, [autoQueueDeadline, queued, queueRequested]);

  useEffect(() => {
    if (autoQueueSeconds !== 0 || queued || queueRequested) return;
    setQueueRequested(true);
    network.joinQueue();
  }, [autoQueueSeconds, network, queued, queueRequested]);

  const handlePlayAgain = () => {
    if (queued || queueRequested) return;
    setQueueRequested(true);
    onPlayAgain();
  };

  if (!result) {
    return (
      <div style={{
        position: 'absolute', inset: 0, zIndex: 20,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: '#05050d', color: '#fff', fontFamily: 'monospace',
      }}>
        {queued || queueRequested ? (
          <MatchmakingStatus
            language={language}
            confirmed={queued}
            position={queueStatus.position}
            playersWaiting={queueStatus.playersWaiting}
            estimatedWait={queueStatus.estimatedWait}
            title={queued
              ? tr('Buscando la siguiente partida...', 'Finding the next match...', 'Recherche de la prochaine partie...')
              : tr('Entrando en la cola...', 'Joining the queue...', "Entrée dans la file d'attente...")}
          />
        ) : (
          <div style={{ fontSize: '18px', color: '#888' }}>
            {tr('Procesando resultado...', 'Processing result...', 'Traitement du résultat...')}
          </div>
        )}
        <button onClick={onMainMenu} style={{
          marginTop: '20px', padding: '10px 26px', fontSize: '14px',
          background: 'transparent', color: '#aaa', border: '1px solid #444',
          borderRadius: '6px', cursor: 'pointer',
        }}>
          {tr('Volver al menú principal', 'Go to main menu', 'Aller au menu principal')}
        </button>
      </div>
    );
  }

  const r = result;
  const won = r.won;
  const isAbandon = r.exitReason === 'voluntary_leave';

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 20,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#05050d', color: '#fff', fontFamily: 'monospace',
      padding: '20px',
    }}>
      <h1 style={{
        fontSize: '42px', marginBottom: '4px',
        color: won ? '#00ff88' : isAbandon ? '#ff8844' : '#ff4444',
      }}>
        {won ? tr('¡Victoria!', 'Victory!', 'Victoire !')
          : isAbandon ? tr('Abandonaste', 'You left', 'Vous avez quitté')
          : tr('Eliminado', 'Eliminated', 'Éliminé')}
      </h1>
      <div style={{ fontSize: '14px', color: '#888', marginBottom: '20px' }}>
        {tr('Posición', 'Position', 'Position')} #{r.placement} {tr('de', 'of', 'de')} {r.totalPlayers}
      </div>

      <div style={{
        width: 'min(90vw, 380px)',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px', padding: '16px', marginBottom: '16px',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
          <div>
            <span style={{ color: '#666' }}>{tr('Expulsiones', 'Eliminations', 'Éliminations')}: </span>
            <span style={{ color: '#ccc' }}>{r.eliminations}</span>
          </div>
          <div>
            <span style={{ color: '#666' }}>{tr('Orbes', 'Orbs', 'Orbes')}: </span>
            <span style={{ color: '#ccc' }}>{r.orbsCollected}</span>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <span style={{ color: '#666' }}>{tr('Supervivencia', 'Survival', 'Survie')}: </span>
            <span style={{ color: '#ccc' }}>{Math.round(r.survivalMs / 1000)}s</span>
          </div>
        </div>
      </div>

      <div style={{
        width: 'min(90vw, 380px)',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px', padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', color: '#aaa' }}>
          {tr('Desglose de monedas', 'Coin breakdown', 'Répartition des pièces')}
        </div>
        <div style={{ fontSize: '12px', lineHeight: 1.8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>{tr('Participación', 'Participation', 'Participation')}</span>
            <span style={{ color: '#00cc66' }}>+{r.rewards.completion}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>Top 10</span>
            <span style={{ color: '#00cc66' }}>+{r.rewards.placementTop10}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>Top 5</span>
            <span style={{ color: '#00cc66' }}>+{r.rewards.placementTop5}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>Top 3</span>
            <span style={{ color: '#00cc66' }}>+{r.rewards.placementTop3}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>{tr('Victoria', 'Victory', 'Victoire')}</span>
            <span style={{ color: '#00cc66' }}>+{r.rewards.victory}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>{tr('Expulsión', 'Elimination', 'Élimination')} (X{r.eliminations})</span>
            <span style={{ color: '#00cc66' }}>+{r.rewards.eliminations}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>{tr('Primera victoria diaria', 'First daily win', 'Première victoire du jour')}</span>
            <span style={{ color: '#ffcc00' }}>+{r.rewards.firstDailyWin}</span>
          </div>
          {r.rewards.abandonPenalty < 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>{tr('Penalización por abandono', 'Leave penalty', 'Pénalité de départ')}</span>
              <span style={{ color: '#ff4444' }}>{r.rewards.abandonPenalty}</span>
            </div>
          )}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '4px',
          }}>
            <span style={{ color: '#fff', fontWeight: 'bold' }}>{tr('Total', 'Total', 'Total')}</span>
            <span style={{
              fontWeight: 'bold',
              color: r.rewards.total >= 0 ? '#00ff88' : '#ff4444',
            }}>
              {r.rewards.total >= 0 ? '+' : ''}{r.rewards.total}
            </span>
          </div>
        </div>
      </div>

      <div style={{
        fontSize: '13px', color: '#666', marginBottom: '20px',
        display: 'flex', gap: '16px',
      }}>
        <span>{tr('Saldo anterior', 'Previous balance', 'Solde précédent')}: {r.balanceBefore}</span>
        <span style={{ color: '#aaa' }}>→</span>
        <span style={{ color: '#00ff88' }}>{tr('Saldo actual', 'Current balance', 'Solde actuel')}: {r.balanceAfter}</span>
      </div>

      {queued || queueRequested ? (
        <MatchmakingStatus
          language={language}
          confirmed={queued}
          position={queueStatus.position}
          playersWaiting={queueStatus.playersWaiting}
          estimatedWait={queueStatus.estimatedWait}
          title={queued
            ? tr('Buscando la siguiente partida...', 'Finding the next match...', 'Recherche de la prochaine partie...')
            : tr('Entrando en la cola...', 'Joining the queue...', "Entrée dans la file d'attente...")}
        />
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>
            {tr('Volver a jugar automáticamente en', 'Auto-queue in', 'File auto dans')}
          </div>
          <div style={{
            fontSize: '44px', lineHeight: 1, fontWeight: 'bold',
            color: autoQueueSeconds <= 5 ? '#ff8844' : '#ffffff',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {autoQueueSeconds}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            {tr('segundos', 'seconds', 'secondes')}
          </div>
        </div>
      )}
      {!queued && !queueRequested && (
        <button onClick={handlePlayAgain} style={{
          marginTop: '20px', padding: '12px 32px', fontSize: '16px',
          background: '#4444ff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer',
        }}>
          {tr('Volver a jugar ahora', 'Play again now', 'Rejouer maintenant')}
        </button>
      )}
      <button onClick={onMainMenu} style={{
        marginTop: '10px', padding: '10px 26px', fontSize: '14px',
        background: 'transparent', color: '#aaa', border: '1px solid #444',
        borderRadius: '6px', cursor: 'pointer',
      }}>
        {tr('Volver al menú principal', 'Go to main menu', 'Aller au menu principal')}
      </button>
    </div>
  );
}
